import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useScopedAnalyticsText } from './analyticsScope';
import { CoverageHistory, UserActivityCounts } from 'src/domain/analytics';
import { FilterTabs } from 'src/components/FilterTabs/FilterTabs';
import { AnalyticsCard } from './AnalyticsCard';
import { CoverageHistoryChart } from './CoverageHistoryChart';
import {
    CurrentCoverageChart,
    ActivityTrackingInfo,
} from './UserActivitySummary';

export const CoverageCard = ({
    population,
    usage,
    total,
    history,
    loading,
    error,
    onNeverLoggedIn,
}: {
    population: 'residents' | 'employees';
    usage?: UserActivityCounts;
    total: number;
    history?: CoverageHistory;
    loading: boolean;
    error?: unknown;
    onNeverLoggedIn?: () => void;
}) => {
    const { t } = useTranslation();
    const text = useScopedAnalyticsText();
    const [view, setView] = useState<'current' | 'monthly'>('current');
    const employee = population === 'employees';
    const currentAvailable = employee
        ? usage?.employeeCoverage
        : usage?.mainContactCoverage;
    return (
        <AnalyticsCard
            info={
                <>
                    {usage ? (
                        <ActivityTrackingInfo usage={usage} />
                    ) : (
                        <p>{t('admin.analytics.activityAppNote')}</p>
                    )}
                    <p>
                        {text(
                            view === 'monthly'
                                ? employee
                                    ? 'admin.analytics.employeeHistoryNote'
                                    : 'admin.analytics.coverageHistoryNote'
                                : employee
                                ? 'admin.analytics.employeeCoverageNote'
                                : 'admin.analytics.mainContactCoverageNote',
                        )}
                    </p>
                </>
            }
            title={t(
                employee
                    ? 'admin.analytics.employeeCoverageTitle'
                    : 'admin.analytics.mainContactCoverageTitle',
            )}
            description={t(
                view === 'monthly'
                    ? employee
                        ? 'admin.analytics.employeeHistoryScope'
                        : 'admin.analytics.coverageHistoryScope'
                    : employee
                    ? 'admin.analytics.employeeCoverageScope'
                    : 'admin.analytics.mainContactCoverageScope',
            )}
            action={
                <div className="analytics-chart-toggle">
                    <FilterTabs
                        label={t('admin.analytics.coverageView')}
                        value={view}
                        options={[
                            {
                                value: 'current',
                                label: t('admin.analytics.coverageCurrent'),
                            },
                            {
                                value: 'monthly',
                                label: t('admin.analytics.coverageMonthly'),
                            },
                        ]}
                        onChange={setView}
                    />
                </div>
            }
        >
            {view === 'monthly' ? (
                <CoverageHistoryChart
                    embedded
                    population={population}
                    data={history}
                    loading={loading}
                    error={error}
                />
            ) : usage && currentAvailable ? (
                <CurrentCoverageChart
                    onNeverLoggedIn={onNeverLoggedIn}
                    embedded
                    population={population}
                    usage={usage}
                    total={total}
                />
            ) : (
                <p className="analytics-note">
                    {t('admin.analytics.trackingNotStarted')}
                </p>
            )}
        </AnalyticsCard>
    );
};
